import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import Sidebar from '../components/Sidebar';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Plus,
  Loader2,
  Search,
  Pencil,
  Trash2,
  FlaskConical,
  Package,
  Star,
  TrendingUp,
  Percent,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LAB_PROVIDERS = [
  { value: 'nphoto', label: 'nPhoto' },
  { value: 'saal', label: 'Saal Digital' },
  { value: 'crystal_albums', label: 'Crystal Albums' },
  { value: 'dglab', label: 'DG Lab' },
];

const PRODUCT_CATEGORIES = [
  { value: 'prints', label: 'Odbitki' },
  { value: 'albums', label: 'Albumy' },
  { value: 'canvas', label: 'Kanwy' },
  { value: 'frames', label: 'Ramki' },
];

const CATEGORY_COLORS = {
  prints: 'bg-blue-500/10 text-blue-600',
  albums: 'bg-purple-500/10 text-purple-600',
  canvas: 'bg-amber-500/10 text-amber-600',
  frames: 'bg-emerald-500/10 text-emerald-600',
};

const emptyLabForm = {
  lab_name: '',
  lab_id: '',
  markup_percent: 0,
  default_lab: false,
  notes: '',
};

const emptyProductForm = {
  lab_id: '',
  category: '',
  name: '',
  sku: '',
  lab_price: 0,
  client_price: 0,
  description: '',
};

export default function LabsPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();

  // Labs state
  const [configs, setConfigs] = useState([]);
  const [loadingConfigs, setLoadingConfigs] = useState(true);
  const [labDialogOpen, setLabDialogOpen] = useState(false);
  const [labForm, setLabForm] = useState({ ...emptyLabForm });
  const [editingLabId, setEditingLabId] = useState(null);
  const [savingLab, setSavingLab] = useState(false);
  const [deletingLabId, setDeletingLabId] = useState(null);

  // Products state
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [productForm, setProductForm] = useState({ ...emptyProductForm });
  const [editingProductId, setEditingProductId] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);
  const [deletingProductId, setDeletingProductId] = useState(null);
  const [productLabFilter, setProductLabFilter] = useState('all');
  const [productSearch, setProductSearch] = useState('');
  const [productCategoryFilter, setProductCategoryFilter] = useState('all');

  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null, name: '' });

  // Fetch labs on mount
  useEffect(() => {
    fetchConfigs();
  }, []);

  // Fetch products when lab filter changes
  useEffect(() => {
    fetchProducts();
  }, [productLabFilter]);

  const fetchConfigs = async () => {
    setLoadingConfigs(true);
    try {
      const response = await axios.get(`${API}/labs/configs`);
      setConfigs(response.data.configs || []);
    } catch (error) {
      toast.error('Nie udalo sie zaladowac konfiguracji laboratoriow');
    } finally {
      setLoadingConfigs(false);
    }
  };

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const params = productLabFilter !== 'all' ? { lab_id: productLabFilter } : {};
      const response = await axios.get(`${API}/labs/products`, { params });
      setProducts(response.data.products || []);
    } catch (error) {
      toast.error('Nie udalo sie zaladowac produktow');
    } finally {
      setLoadingProducts(false);
    }
  };

  // ── Lab CRUD ──────────────────────────────────────────────────────────

  const openLabDialog = (lab = null) => {
    if (lab) {
      setEditingLabId(lab.id);
      setLabForm({
        lab_name: lab.lab_name || '',
        lab_id: lab.lab_id || '',
        markup_percent: lab.markup_percent || 0,
        default_lab: lab.default_lab || false,
        notes: lab.notes || '',
      });
    } else {
      setEditingLabId(null);
      setLabForm({ ...emptyLabForm });
    }
    setLabDialogOpen(true);
  };

  const handleSaveLab = async (e) => {
    e.preventDefault();
    if (!labForm.lab_name.trim()) {
      toast.error('Nazwa laboratorium jest wymagana');
      return;
    }
    if (!labForm.lab_id) {
      toast.error('Wybierz dostawce laboratorium');
      return;
    }

    setSavingLab(true);
    try {
      if (editingLabId) {
        const response = await axios.put(`${API}/labs/configs/${editingLabId}`, labForm);
        setConfigs(configs.map((c) => (c.id === editingLabId ? response.data : c)));
        toast.success('Laboratorium zostalo zaktualizowane');
      } else {
        const response = await axios.post(`${API}/labs/configs`, labForm);
        setConfigs([...configs, response.data]);
        toast.success('Laboratorium zostalo dodane');
      }
      setLabDialogOpen(false);
      setLabForm({ ...emptyLabForm });
      setEditingLabId(null);
    } catch (error) {
      toast.error(
        editingLabId
          ? 'Nie udalo sie zaktualizowac laboratorium'
          : 'Nie udalo sie dodac laboratorium'
      );
    } finally {
      setSavingLab(false);
    }
  };

  const handleDeleteLab = async (id) => {
    setDeletingLabId(id);
    try {
      await axios.delete(`${API}/labs/configs/${id}`);
      setConfigs(configs.filter((c) => c.id !== id));
      toast.success('Laboratorium zostalo usuniete');
    } catch (error) {
      toast.error('Nie udalo sie usunac laboratorium');
    } finally {
      setDeletingLabId(null);
      setDeleteDialog({ open: false, type: null, id: null, name: '' });
    }
  };

  // ── Product CRUD ──────────────────────────────────────────────────────

  const openProductDialog = (product = null) => {
    if (product) {
      setEditingProductId(product.id);
      setProductForm({
        lab_id: product.lab_id || '',
        category: product.category || '',
        name: product.name || '',
        sku: product.sku || '',
        lab_price: product.lab_price || 0,
        client_price: product.client_price || 0,
        description: product.description || '',
      });
    } else {
      setEditingProductId(null);
      setProductForm({
        ...emptyProductForm,
        lab_id: productLabFilter !== 'all' ? productLabFilter : '',
      });
    }
    setProductDialogOpen(true);
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    if (!productForm.name.trim()) {
      toast.error('Nazwa produktu jest wymagana');
      return;
    }
    if (!productForm.lab_id) {
      toast.error('Wybierz laboratorium');
      return;
    }
    if (!productForm.category) {
      toast.error('Wybierz kategorie');
      return;
    }

    setSavingProduct(true);
    try {
      if (editingProductId) {
        const response = await axios.put(`${API}/labs/products/${editingProductId}`, productForm);
        setProducts(products.map((p) => (p.id === editingProductId ? response.data : p)));
        toast.success('Produkt zostal zaktualizowany');
      } else {
        const response = await axios.post(`${API}/labs/products`, productForm);
        setProducts([...products, response.data]);
        toast.success('Produkt zostal dodany');
      }
      setProductDialogOpen(false);
      setProductForm({ ...emptyProductForm });
      setEditingProductId(null);
    } catch (error) {
      toast.error(
        editingProductId
          ? 'Nie udalo sie zaktualizowac produktu'
          : 'Nie udalo sie dodac produktu'
      );
    } finally {
      setSavingProduct(false);
    }
  };

  const handleDeleteProduct = async (id) => {
    setDeletingProductId(id);
    try {
      await axios.delete(`${API}/labs/products/${id}`);
      setProducts(products.filter((p) => p.id !== id));
      toast.success('Produkt zostal usuniety');
    } catch (error) {
      toast.error('Nie udalo sie usunac produktu');
    } finally {
      setDeletingProductId(null);
      setDeleteDialog({ open: false, type: null, id: null, name: '' });
    }
  };

  const handleToggleProductActive = async (product) => {
    try {
      const response = await axios.put(`${API}/labs/products/${product.id}`, {
        ...product,
        active: !product.active,
      });
      setProducts(products.map((p) => (p.id === product.id ? response.data : p)));
      toast.success(
        response.data.active ? 'Produkt aktywowany' : 'Produkt dezaktywowany'
      );
    } catch (error) {
      toast.error('Nie udalo sie zmienic statusu produktu');
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────

  const getLabLabel = (labId) => {
    const provider = LAB_PROVIDERS.find((p) => p.value === labId);
    return provider ? provider.label : labId;
  };

  const getLabNameById = (labId) => {
    const config = configs.find((c) => c.lab_id === labId);
    return config ? config.lab_name : getLabLabel(labId);
  };

  const getCategoryLabel = (category) => {
    const cat = PRODUCT_CATEGORIES.find((c) => c.value === category);
    return cat ? cat.label : category;
  };

  const calculateMargin = (labPrice, clientPrice) => {
    if (!labPrice || labPrice === 0) return 0;
    return (((clientPrice - labPrice) / labPrice) * 100).toFixed(1);
  };

  const calculateProfit = (labPrice, clientPrice) => {
    return ((clientPrice || 0) - (labPrice || 0)).toFixed(2);
  };

  const filteredProducts = products.filter((p) => {
    const matchesSearch =
      p.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
      p.sku?.toLowerCase().includes(productSearch.toLowerCase());
    const matchesCategory =
      productCategoryFilter === 'all' || p.category === productCategoryFilter;
    return matchesSearch && matchesCategory;
  });

  // ── Stats ─────────────────────────────────────────────────────────────

  const totalProducts = products.length;
  const activeProducts = products.filter((p) => p.active !== false).length;
  const avgMargin =
    products.length > 0
      ? (
          products.reduce(
            (sum, p) => sum + parseFloat(calculateMargin(p.lab_price, p.client_price)),
            0
          ) / products.length
        ).toFixed(1)
      : '0.0';

  // ── Loading ───────────────────────────────────────────────────────────

  if (loadingConfigs) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/labs" />

      <main className="lg:ml-64 p-6 lg:p-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Laboratoria i produkty</h1>
          <p className="text-muted-foreground mt-1">
            Zarzadzaj integracjami z laboratoriami i cennikiem produktow
          </p>
        </div>

        <Tabs defaultValue="labs" className="space-y-6">
          <TabsList className="bg-secondary">
            <TabsTrigger value="labs" className="gap-2">
              <FlaskConical className="w-4 h-4" />
              Laboratoria
            </TabsTrigger>
            <TabsTrigger value="products" className="gap-2">
              <Package className="w-4 h-4" />
              Produkty
            </TabsTrigger>
          </TabsList>

          {/* ═══════════════════ LABS TAB ═══════════════════ */}
          <TabsContent value="labs">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Add Lab Button */}
              <div className="flex justify-end">
                <Button onClick={() => openLabDialog()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Dodaj laboratorium
                </Button>
              </div>

              {/* Lab Cards */}
              {configs.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {configs.map((lab, idx) => (
                    <motion.div
                      key={lab.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: idx * 0.05 }}
                    >
                      <Card className="bg-card border-border relative group">
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <CardTitle className="text-lg flex items-center gap-2">
                                {lab.lab_name}
                                {lab.default_lab && (
                                  <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20">
                                    <Star className="w-3 h-3 mr-1" />
                                    Domyslne
                                  </Badge>
                                )}
                              </CardTitle>
                              <p className="text-sm text-muted-foreground">
                                {getLabLabel(lab.lab_id)}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openLabDialog(lab)}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() =>
                                  setDeleteDialog({
                                    open: true,
                                    type: 'lab',
                                    id: lab.id,
                                    name: lab.lab_name,
                                  })
                                }
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                            <span className="text-sm text-muted-foreground">Narzut</span>
                            <span className="text-lg font-bold flex items-center gap-1">
                              <Percent className="w-4 h-4 text-muted-foreground" />
                              {lab.markup_percent || 0}%
                            </span>
                          </div>
                          {lab.notes && (
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {lab.notes}
                            </p>
                          )}
                          <div className="text-xs text-muted-foreground">
                            ID: <span className="font-mono">{lab.lab_id}</span>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <Card className="bg-card border-border">
                  <CardContent className="p-12 text-center">
                    <FlaskConical className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-lg font-medium mb-2">Brak laboratoriow</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Dodaj pierwsze laboratorium, aby rozpoczac zarzadzanie produktami
                    </p>
                    <Button onClick={() => openLabDialog()} className="mt-2">
                      <Plus className="w-4 h-4 mr-2" />
                      Dodaj laboratorium
                    </Button>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          </TabsContent>

          {/* ═══════════════════ PRODUCTS TAB ═══════════════════ */}
          <TabsContent value="products">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {/* Stats Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="bg-card border-border">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Wszystkie produkty</p>
                        <p className="text-2xl font-bold">{totalProducts}</p>
                      </div>
                      <Package className="w-8 h-8 text-muted-foreground/50" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Aktywne</p>
                        <p className="text-2xl font-bold text-green-600">{activeProducts}</p>
                      </div>
                      <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-card border-border">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Srednia marza</p>
                        <p className="text-2xl font-bold">{avgMargin}%</p>
                      </div>
                      <TrendingUp className="w-8 h-8 text-muted-foreground/50" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Szukaj produktow..."
                    value={productSearch}
                    onChange={(e) => setProductSearch(e.target.value)}
                    className="pl-10 h-12"
                  />
                </div>
                <Select value={productLabFilter} onValueChange={setProductLabFilter}>
                  <SelectTrigger className="w-[200px] h-12">
                    <SelectValue placeholder="Laboratorium" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Wszystkie laboratoria</SelectItem>
                    {configs.map((c) => (
                      <SelectItem key={c.lab_id} value={c.lab_id}>
                        {c.lab_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={productCategoryFilter} onValueChange={setProductCategoryFilter}>
                  <SelectTrigger className="w-[180px] h-12">
                    <SelectValue placeholder="Kategoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Wszystkie kategorie</SelectItem>
                    {PRODUCT_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button onClick={() => openProductDialog()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Dodaj produkt
                </Button>
              </div>

              {/* Products Table */}
              {loadingProducts ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              ) : filteredProducts.length > 0 ? (
                <Card className="bg-card border-border">
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="pl-6">Nazwa</TableHead>
                          <TableHead>SKU</TableHead>
                          <TableHead>Kategoria</TableHead>
                          <TableHead>Laboratorium</TableHead>
                          <TableHead className="text-right">Cena labo</TableHead>
                          <TableHead className="text-right">Cena klienta</TableHead>
                          <TableHead className="text-right">Zysk</TableHead>
                          <TableHead className="text-right">Marza</TableHead>
                          <TableHead className="text-center">Aktywny</TableHead>
                          <TableHead className="text-center">Akcje</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredProducts.map((product, idx) => {
                          const profit = calculateProfit(product.lab_price, product.client_price);
                          const margin = calculateMargin(product.lab_price, product.client_price);
                          const profitNum = parseFloat(profit);
                          const marginNum = parseFloat(margin);

                          return (
                            <motion.tr
                              key={product.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.2, delay: idx * 0.02 }}
                              className="border-b transition-colors hover:bg-muted/50"
                            >
                              <TableCell className="pl-6 py-4">
                                <span className="font-medium">{product.name}</span>
                              </TableCell>
                              <TableCell>
                                <span className="font-mono text-xs text-muted-foreground">
                                  {product.sku || '-'}
                                </span>
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant="outline"
                                  className={CATEGORY_COLORS[product.category] || ''}
                                >
                                  {getCategoryLabel(product.category)}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm text-muted-foreground">
                                  {getLabNameById(product.lab_id)}
                                </span>
                              </TableCell>
                              <TableCell className="text-right font-mono text-sm">
                                {(product.lab_price || 0).toFixed(2)} PLN
                              </TableCell>
                              <TableCell className="text-right font-mono text-sm font-semibold">
                                {(product.client_price || 0).toFixed(2)} PLN
                              </TableCell>
                              <TableCell className="text-right">
                                <span
                                  className={`font-mono text-sm font-semibold ${
                                    profitNum > 0
                                      ? 'text-green-600'
                                      : profitNum < 0
                                      ? 'text-red-600'
                                      : 'text-muted-foreground'
                                  }`}
                                >
                                  {profitNum > 0 ? '+' : ''}
                                  {profit} PLN
                                </span>
                              </TableCell>
                              <TableCell className="text-right">
                                <Badge
                                  variant="outline"
                                  className={
                                    marginNum >= 50
                                      ? 'bg-green-500/10 text-green-600 border-green-500/20'
                                      : marginNum >= 20
                                      ? 'bg-amber-500/10 text-amber-600 border-amber-500/20'
                                      : marginNum > 0
                                      ? 'bg-orange-500/10 text-orange-600 border-orange-500/20'
                                      : 'bg-red-500/10 text-red-600 border-red-500/20'
                                  }
                                >
                                  {margin}%
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <Switch
                                  checked={product.active !== false}
                                  onCheckedChange={() => handleToggleProductActive(product)}
                                />
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="flex items-center justify-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openProductDialog(product)}
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-destructive hover:text-destructive"
                                    onClick={() =>
                                      setDeleteDialog({
                                        open: true,
                                        type: 'product',
                                        id: product.id,
                                        name: product.name,
                                      })
                                    }
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </motion.tr>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-card border-border">
                  <CardContent className="p-12 text-center">
                    <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-lg font-medium mb-2">
                      {productSearch || productLabFilter !== 'all' || productCategoryFilter !== 'all'
                        ? 'Nie znaleziono produktow'
                        : 'Brak produktow'}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {productSearch || productLabFilter !== 'all' || productCategoryFilter !== 'all'
                        ? 'Sprobuj zmienic kryteria wyszukiwania'
                        : 'Dodaj pierwszy produkt, aby rozpoczac'}
                    </p>
                    {!productSearch && productLabFilter === 'all' && productCategoryFilter === 'all' && (
                      <Button onClick={() => openProductDialog()} className="mt-2">
                        <Plus className="w-4 h-4 mr-2" />
                        Dodaj produkt
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}
            </motion.div>
          </TabsContent>
        </Tabs>
      </main>

      {/* ═══════════════════ LAB DIALOG ═══════════════════ */}
      <Dialog open={labDialogOpen} onOpenChange={setLabDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingLabId ? 'Edytuj laboratorium' : 'Nowe laboratorium'}
            </DialogTitle>
            <DialogDescription>
              {editingLabId
                ? 'Zaktualizuj dane laboratorium'
                : 'Dodaj nowe laboratorium do swojej konfiguracji'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveLab} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="lab_name">Nazwa laboratorium *</Label>
              <Input
                id="lab_name"
                placeholder="np. Moje nPhoto"
                value={labForm.lab_name}
                onChange={(e) => setLabForm({ ...labForm, lab_name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Dostawca *</Label>
              <Select
                value={labForm.lab_id}
                onValueChange={(v) => setLabForm({ ...labForm, lab_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Wybierz dostawce" />
                </SelectTrigger>
                <SelectContent>
                  {LAB_PROVIDERS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="markup_percent">Narzut (%)</Label>
              <Input
                id="markup_percent"
                type="number"
                min="0"
                max="500"
                step="1"
                value={labForm.markup_percent}
                onChange={(e) =>
                  setLabForm({ ...labForm, markup_percent: parseFloat(e.target.value) || 0 })
                }
              />
              <p className="text-xs text-muted-foreground">
                Domyslny narzut stosowany do cen laboratoryjnych przy wyliczaniu cen klienta
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="lab_notes">Notatki</Label>
              <Input
                id="lab_notes"
                placeholder="Dodatkowe informacje..."
                value={labForm.notes}
                onChange={(e) => setLabForm({ ...labForm, notes: e.target.value })}
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="space-y-0.5">
                <Label htmlFor="default_lab" className="text-sm font-medium cursor-pointer">
                  Domyslne laboratorium
                </Label>
                <p className="text-xs text-muted-foreground">
                  Ustaw jako glowne laboratorium dla nowych zamowien
                </p>
              </div>
              <Switch
                id="default_lab"
                checked={labForm.default_lab}
                onCheckedChange={(checked) => setLabForm({ ...labForm, default_lab: checked })}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setLabDialogOpen(false)}
              >
                Anuluj
              </Button>
              <Button type="submit" disabled={savingLab}>
                {savingLab && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingLabId ? 'Zapisz zmiany' : 'Dodaj laboratorium'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ═══════════════════ PRODUCT DIALOG ═══════════════════ */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProductId ? 'Edytuj produkt' : 'Nowy produkt'}
            </DialogTitle>
            <DialogDescription>
              {editingProductId
                ? 'Zaktualizuj dane produktu'
                : 'Dodaj nowy produkt do cennika'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveProduct} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Laboratorium *</Label>
                <Select
                  value={productForm.lab_id}
                  onValueChange={(v) => setProductForm({ ...productForm, lab_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Wybierz" />
                  </SelectTrigger>
                  <SelectContent>
                    {configs.map((c) => (
                      <SelectItem key={c.lab_id} value={c.lab_id}>
                        {c.lab_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Kategoria *</Label>
                <Select
                  value={productForm.category}
                  onValueChange={(v) => setProductForm({ ...productForm, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Wybierz" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRODUCT_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="product_name">Nazwa produktu *</Label>
              <Input
                id="product_name"
                placeholder="np. Odbitka 13x18 mat"
                value={productForm.name}
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="product_sku">SKU</Label>
              <Input
                id="product_sku"
                placeholder="np. PRINT-13x18-MAT"
                value={productForm.sku}
                onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="lab_price">Cena laboratorium (PLN) *</Label>
                <Input
                  id="lab_price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={productForm.lab_price}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      lab_price: parseFloat(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client_price">Cena klienta (PLN) *</Label>
                <Input
                  id="client_price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={productForm.client_price}
                  onChange={(e) =>
                    setProductForm({
                      ...productForm,
                      client_price: parseFloat(e.target.value) || 0,
                    })
                  }
                />
              </div>
            </div>

            {/* Profit margin calculator */}
            {(productForm.lab_price > 0 || productForm.client_price > 0) && (
              <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Kalkulator marzy</span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-xs text-muted-foreground">Koszt labo</p>
                    <p className="font-mono text-sm font-semibold">
                      {(productForm.lab_price || 0).toFixed(2)} PLN
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Zysk</p>
                    <p
                      className={`font-mono text-sm font-bold ${
                        parseFloat(calculateProfit(productForm.lab_price, productForm.client_price)) > 0
                          ? 'text-green-600'
                          : parseFloat(calculateProfit(productForm.lab_price, productForm.client_price)) < 0
                          ? 'text-red-600'
                          : ''
                      }`}
                    >
                      {parseFloat(calculateProfit(productForm.lab_price, productForm.client_price)) > 0 ? '+' : ''}
                      {calculateProfit(productForm.lab_price, productForm.client_price)} PLN
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Marza</p>
                    <p
                      className={`font-mono text-sm font-bold ${
                        parseFloat(calculateMargin(productForm.lab_price, productForm.client_price)) >= 50
                          ? 'text-green-600'
                          : parseFloat(calculateMargin(productForm.lab_price, productForm.client_price)) >= 20
                          ? 'text-amber-600'
                          : 'text-red-600'
                      }`}
                    >
                      {calculateMargin(productForm.lab_price, productForm.client_price)}%
                    </p>
                  </div>
                </div>
                {parseFloat(calculateMargin(productForm.lab_price, productForm.client_price)) < 20 &&
                  productForm.lab_price > 0 && (
                    <div className="flex items-center gap-2 text-xs text-amber-600 mt-2">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      Marza ponizej 20% — rozwaZ podwyzszenie ceny klienta
                    </div>
                  )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="product_description">Opis</Label>
              <Input
                id="product_description"
                placeholder="Krotki opis produktu..."
                value={productForm.description}
                onChange={(e) =>
                  setProductForm({ ...productForm, description: e.target.value })
                }
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setProductDialogOpen(false)}
              >
                Anuluj
              </Button>
              <Button type="submit" disabled={savingProduct}>
                {savingProduct && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingProductId ? 'Zapisz zmiany' : 'Dodaj produkt'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ═══════════════════ DELETE CONFIRMATION ═══════════════════ */}
      <Dialog
        open={deleteDialog.open}
        onOpenChange={(open) => {
          if (!open) setDeleteDialog({ open: false, type: null, id: null, name: '' });
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Potwierdz usuniecie</DialogTitle>
            <DialogDescription>
              Czy na pewno chcesz usunac{' '}
              {deleteDialog.type === 'lab' ? 'laboratorium' : 'produkt'}{' '}
              <strong>{deleteDialog.name}</strong>? Tej operacji nie mozna cofnac.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, type: null, id: null, name: '' })}
            >
              Anuluj
            </Button>
            <Button
              variant="destructive"
              disabled={deletingLabId === deleteDialog.id || deletingProductId === deleteDialog.id}
              onClick={() => {
                if (deleteDialog.type === 'lab') {
                  handleDeleteLab(deleteDialog.id);
                } else {
                  handleDeleteProduct(deleteDialog.id);
                }
              }}
            >
              {(deletingLabId === deleteDialog.id || deletingProductId === deleteDialog.id) && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Usun
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
